import { AppModuleCode, PermissionCode, type AuthUser } from '@gb-ticket/shared-types';

import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import {
  DASHBOARD_ARBEIT_WIDGET_IDS,
  DASHBOARD_LAGEBILD_WIDGET_IDS,
  DASHBOARD_WIDGET_IDS,
  DEFAULT_DASHBOARD_ZONE_ORDER,
  type DashboardWidgetId,
  type DashboardZoneId,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';

export type DashboardWidgetDef = {
  id: DashboardWidgetId;
  zone: DashboardZoneId;
  title: string;
  description?: string;
  /** Default in a fresh layout */
  defaultEnabled: boolean;
  /** RBAC / system module — false ⇒ never showable */
  isAvailable: (user: AuthUser) => boolean;
  /** Banner vs card tile */
  layoutHint?: 'banner' | 'card';
};

export type DashboardZoneDef = {
  id: DashboardZoneId;
  title: string;
  description?: string;
  defaultEnabled: boolean;
};

export const DASHBOARD_ZONE_DEFS: readonly DashboardZoneDef[] = [
  {
    id: 'arbeit',
    title: 'Arbeit',
    description: 'Persönliche Ticket-Schlangen und Signale.',
    defaultEnabled: true,
  },
  {
    id: 'lagebild',
    title: 'IT-Lagebild',
    description: 'Domänen-Übersicht — Kennzahlen aus Tickets und Inventar (Europe/Berlin).',
    defaultEnabled: true,
  },
] as const;

function alwaysAvailable(_user: AuthUser): boolean {
  return true;
}

export const DASHBOARD_WIDGET_DEFS: readonly DashboardWidgetDef[] = [
  {
    id: 'work-major-incidents',
    zone: 'arbeit',
    title: 'Major Incidents',
    description: 'Aktive Major-Störungen.',
    defaultEnabled: true,
    layoutHint: 'banner',
    isAvailable: (user) => isModuleEnabled(user, AppModuleCode.INCIDENTS),
  },
  {
    id: 'work-mine',
    zone: 'arbeit',
    title: 'Meine offenen Tickets',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
  {
    id: 'work-unassigned',
    zone: 'arbeit',
    title: 'Nicht zugewiesen',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: (user) => hasPermission(user, PermissionCode.TICKETS_ASSIGN),
  },
  {
    id: 'work-sla-risk',
    zone: 'arbeit',
    title: 'SLA-Risiko',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: (user) => isModuleEnabled(user, AppModuleCode.SLA),
  },
  {
    id: 'work-waiting',
    zone: 'arbeit',
    title: 'Wartet auf Kunde',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
  {
    id: 'work-upcoming-changes',
    zone: 'arbeit',
    title: 'Kommende Änderungen',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: (user) => isModuleEnabled(user, AppModuleCode.CHANGES),
  },
  {
    id: 'security',
    zone: 'lagebild',
    title: 'Security',
    description: 'Deep-Links zu Entra / M365 / Microsoft Security.',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
  {
    id: 'assets-licenses',
    zone: 'lagebild',
    title: 'Lizenzen & Assets',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: (user) =>
      hasPermission(user, PermissionCode.ASSETS_READ) &&
      isModuleEnabled(user, AppModuleCode.ASSETS),
  },
  {
    id: 'helpdesk',
    zone: 'lagebild',
    title: 'Tickets / Helpdesk',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
  {
    id: 'top-problems',
    zone: 'lagebild',
    title: 'Top Probleme',
    description: 'Top Probleme diesen Monat.',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: (user) =>
      hasPermission(user, PermissionCode.TICKETS_READ_ALL) &&
      isModuleEnabled(user, AppModuleCode.PROBLEMS),
  },
  {
    id: 'network',
    zone: 'lagebild',
    title: 'Netzwerk & Infrastruktur',
    description: 'Firewall, VPN und Netzgeräte.',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
  {
    id: 'server-storage',
    zone: 'lagebild',
    title: 'Server & Storage',
    defaultEnabled: true,
    layoutHint: 'card',
    isAvailable: alwaysAvailable,
  },
] as const;

const widgetDefById = new Map<DashboardWidgetId, DashboardWidgetDef>(
  DASHBOARD_WIDGET_DEFS.map((def) => [def.id, def]),
);

const zoneDefById = new Map<DashboardZoneId, DashboardZoneDef>(
  DASHBOARD_ZONE_DEFS.map((def) => [def.id, def]),
);

export function getDashboardWidgetDef(id: DashboardWidgetId): DashboardWidgetDef {
  const def = widgetDefById.get(id);
  if (!def) {
    throw new Error(`Unknown dashboard widget: ${id}`);
  }
  return def;
}

export function getDashboardZoneDef(id: DashboardZoneId): DashboardZoneDef {
  const def = zoneDefById.get(id);
  if (!def) {
    throw new Error(`Unknown dashboard zone: ${id}`);
  }
  return def;
}

export function dashboardWidgetDefaultEnabledMap(): Record<DashboardWidgetId, boolean> {
  return Object.fromEntries(
    DASHBOARD_WIDGET_DEFS.map((def) => [def.id, def.defaultEnabled]),
  ) as Record<DashboardWidgetId, boolean>;
}

/** Widgets available to this user (RBAC + system modules), regardless of layout prefs. */
export function availableDashboardWidgets(user: AuthUser): DashboardWidgetDef[] {
  return DASHBOARD_WIDGET_DEFS.filter((def) => def.isAvailable(user));
}

export function availableDashboardWidgetIds(user: AuthUser): DashboardWidgetId[] {
  return availableDashboardWidgets(user).map((def) => def.id);
}

/** Default widget order per zone (today's UI), for assertions / docs. */
export const DEFAULT_DASHBOARD_WIDGET_ORDER = {
  arbeit: [...DASHBOARD_ARBEIT_WIDGET_IDS],
  lagebild: [...DASHBOARD_LAGEBILD_WIDGET_IDS],
} as const satisfies Record<DashboardZoneId, readonly DashboardWidgetId[]>;

export { DEFAULT_DASHBOARD_ZONE_ORDER, DASHBOARD_WIDGET_IDS };
